"use client"
import React, { useState } from "react";
import { useTranslations } from 'next-intl';
import config from "@/config";
import ButtonCheckout from "@/components/ButtonCheckout";

const CreditPacks = () => {
  const t = useTranslations('Pricing.creditPacks');
  const tGeneral = useTranslations('Pricing.general');
  const [selectedPackIndex, setSelectedPackIndex] = useState(config.creditPacks.popularIndex);
  const packs = config.creditPacks.packs;
  const popularIndex = config.creditPacks.popularIndex;

  // 格式化积分数量显示
  const formatCredits = (credits) => {
    if (credits >= 1000) {
      return (credits / 1000).toFixed(credits % 1000 === 0 ? 0 : 1) + 'K';
    }
    return credits.toString();
  };

  // 计算每100个积分的价格（用于显示性价比）
  const getCreditValue = (pack) => {
    return ((pack.price / pack.credits) * 100).toFixed(2);
  };

  // 获取最佳性价比的包
  const getBestValueIndex = () => {
    let bestIndex = 0;
    let bestValue = getCreditValue(packs[0]);
    
    packs.forEach((pack, index) => {
      const value = getCreditValue(pack);
      if (parseFloat(value) < parseFloat(bestValue)) {
        bestValue = value;
        bestIndex = index;
      }
    });
    
    return bestIndex;
  };

  const bestValueIndex = getBestValueIndex();
  const selectedPack = packs[selectedPackIndex];

  return (
    <div className="py-2 md:py-3 bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="max-w-5xl mx-auto px-3 md:px-4">
        
        {/* 滑块控制器 */}
        <div className="mb-3 md:mb-4">
          <div className="max-w-3xl mx-auto">
            {/* 滑块 */}
            <div className="relative mb-3">
              <input
                type="range"
                min="0"
                max={packs.length - 1}
                value={selectedPackIndex}
                onChange={(e) => setSelectedPackIndex(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, 
                    rgb(59, 130, 246) 0%, 
                    rgb(59, 130, 246) ${(selectedPackIndex / (packs.length - 1)) * 100}%, 
                    rgb(55, 65, 81) ${(selectedPackIndex / (packs.length - 1)) * 100}%, 
                    rgb(55, 65, 81) 100%)`
                }}
              />
              
              {/* 滑块标记点 */}
              <div className="absolute top-0 w-full h-2 pointer-events-none">
                {packs.map((_, index) => (
                  <div
                    key={index}
                    className={`absolute w-3 h-3 md:w-4 md:h-4 rounded-full border-2 border-white transform -translate-x-1/2 -translate-y-1 ${
                      index === selectedPackIndex 
                        ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-lg scale-125' 
                        : 'bg-gray-600'
                    }`}
                    style={{ left: `${(index / (packs.length - 1)) * 100}%` }}
                  />
                ))}
              </div>
            </div>

            {/* 滑块标签 */}
            <div className="flex justify-between text-xs text-gray-400 mb-3 md:mb-4">
              {packs.map((pack, index) => (
                <div key={index} className="text-center">
                  <div className={`font-medium ${
                    index === selectedPackIndex ? 'text-green-400' : ''
                  }`}>
                    {formatCredits(pack.credits)}
                  </div>
                  <div className="text-xs">{t('creditsLabel').toLowerCase()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 选中的包详细信息 */}
        <div className="max-w-xl mx-auto mb-3 md:mb-4">
          <div className={`relative p-3 md:p-4 rounded-xl transition-all duration-500 ${
            selectedPackIndex === popularIndex
              ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-green-500 shadow-2xl shadow-green-500/20'
              : selectedPackIndex === bestValueIndex
              ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-green-500/70 shadow-2xl shadow-green-500/15'
              : 'bg-gray-800 border border-gray-700 shadow-xl'
          } backdrop-blur-sm`}>
            
            {/* 标签 */}
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 flex gap-2">
              {selectedPackIndex === popularIndex && (
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-lg">
                  🔥 {t('mostPopular')}
                </div>
              )}
              {selectedPackIndex === bestValueIndex && selectedPackIndex !== popularIndex && (
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-lg">
                  💎 {t('bestValue')}
                </div>
              )}
            </div>

            <div className="relative z-10 text-center">
              {/* 包名称 */}
              <h3 className={`text-base md:text-lg font-bold mb-1 ${
                selectedPackIndex === popularIndex
                  ? 'bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text'
                  : 'text-white'
              }`}>
                {t(`packs.${selectedPack.name}.name`) || selectedPack.name}
              </h3>

              {/* 描述 */}
              <p className="text-gray-400 text-xs md:text-sm mb-3">{t(`packs.${selectedPack.name}.description`) || 'Perfect for your needs'}</p>

              {/* 积分和价格 */}
              <div className="flex justify-center items-center gap-3 md:gap-4 mb-3">
                {/* 积分 */}
                <div className={`text-center p-2 md:p-3 rounded-lg ${
                  selectedPackIndex === popularIndex
                    ? 'bg-gray-800/70 border border-green-500/30'
                    : selectedPackIndex === bestValueIndex
                    ? 'bg-gray-800/70 border border-gray-600/50'
                    : 'bg-gray-800/50 border border-gray-700/50'
                }`}>
                  <div className={`text-lg md:text-2xl font-bold ${
                    selectedPackIndex === popularIndex
                      ? 'bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text'
                      : 'text-white'
                  }`}>
                    {formatCredits(selectedPack.credits)}
                  </div>
                  <div className="text-gray-400 text-xs">{t('creditsLabel')}</div>
                </div>

                {/* 箭头 */}
                <div className="text-gray-400">
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>

                {/* 价格 */}
                <div className="text-center">
                  <div className={`text-lg md:text-2xl font-bold ${
                    selectedPackIndex === popularIndex
                      ? 'bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text'
                      : 'text-white'
                  }`}>
                    ${selectedPack.price}
                  </div>
                  <div className="text-gray-400 text-xs">{t('oneTimePayment')}</div>
                </div>
              </div>

              {/* 性价比信息 - 突出显示 */}
              <div className="mb-3 text-center p-2 rounded-lg bg-green-900/30 border border-green-500/40">
                <span className="text-green-300 text-sm font-medium">
                  {t('valueComparison', { price: getCreditValue(selectedPack) })}
                  <span className={`${selectedPackIndex === bestValueIndex ? 'text-green-400 font-bold' : ''}`}>
                    {selectedPackIndex === bestValueIndex ? ` • ${t('bestValueText')}` : ''}
                  </span>
                </span>
              </div>
                
              {/* 升级建议 - 弱化显示 */}
              {selectedPackIndex < packs.length - 1 && (
                <button 
                  onClick={() => setSelectedPackIndex(selectedPackIndex + 1)}
                  className="w-full text-center transition-all mb-3 group py-1 mt-2"
                >
                  <div className="flex items-center justify-between text-xs text-gray-500 group-hover:text-gray-400">
                    <div className="font-medium">
                      {tGeneral('upgradePrompt.upgradeTo')} {t(`packs.${packs[selectedPackIndex + 1].name}.name`) || packs[selectedPackIndex + 1].name}
                    </div>
                    <div className="font-bold text-green-500/80 group-hover:text-green-500">
                      {tGeneral('upgradePrompt.save')} ${(parseFloat(getCreditValue(selectedPack)) - parseFloat(getCreditValue(packs[selectedPackIndex + 1]))).toFixed(2)}/100 credits →
                    </div>
                  </div>
                </button>
              )}

              {/* 购买按钮 */}
              <ButtonCheckout
                plan={{
                  name: selectedPack.name,
                  price: selectedPack.price,
                  credits: selectedPack.credits,
                  paymentType: selectedPack.paymentType,
                  priceId: selectedPack.priceId,
                  isOneTime: true
                }}
                isYearly={false}
                className={`w-full py-2 md:py-2.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-xl active:scale-95 ${
                  selectedPackIndex === popularIndex
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-green-500/40 hover:shadow-green-500/60'
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white shadow-gray-700/40'
                }`}
              >
                <span className="flex items-center justify-center">
                  {t('buyCredits', { credits: formatCredits(selectedPack.credits) })}
                  <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </ButtonCheckout>
            </div>
          </div>
        </div>

        {/* 所有包的对比网格（移动端隐藏，桌面端显示） */}

        {/* 特性说明 - 简化版 */}
        <div className="text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-base font-bold text-white mb-3">{t('whyChoosePacks')}</h3>
            
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-2 md:p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
                <div className="w-8 h-8 mx-auto mb-2 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h4 className="font-bold text-white mb-1 text-xs md:text-sm">{t('noSubscription')}</h4>
                <p className="text-gray-400 text-xs">{t('noSubscriptionDesc')}</p>
              </div>
              
              <div className="p-2 md:p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
                <div className="w-8 h-8 mx-auto mb-2 bg-gradient-to-r from-gray-600 to-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="font-bold text-white mb-1 text-xs md:text-sm">{t('neverExpire')}</h4>
                <p className="text-gray-400 text-xs">{t('neverExpireDesc')}</p>
              </div>
              
              <div className="p-2 md:p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
                <div className="w-8 h-8 mx-auto mb-2 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="font-bold text-white mb-1 text-xs md:text-sm">{t('betterValue')}</h4>
                <p className="text-gray-400 text-xs">{t('betterValueDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 自定义滑块样式 */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: linear-gradient(45deg, #3b82f6, #1e40af);
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          border: 2px solid white;
        }
        
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: linear-gradient(45deg, #3b82f6, #1e40af);
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          border: 2px solid white;
        }
      `}</style>
    </div>
  );
};

export default CreditPacks; 